// Challenge flow — static pricing/options data
// Mirrors the seeded challenge_tiers and trading_platforms data

import type { TierName, SizeKey, PlatformKey } from './store';

export interface TierOption {
  tier: TierName;
  tagline: string;
  features: string[];
  bestFor: string;
  badge?: string;
}

export const TIER_OPTIONS: TierOption[] = [
  {
    tier: 'Beginner',
    tagline: 'For traders getting started with evaluation accounts',
    bestFor: 'New prop traders',
    features: [
      'EOD Balance drawdown',
      'Up to 80% profit split',
      '5 funded days minimum',
      'No activation fee',
    ],
  },
  {
    tier: 'Mid',
    tagline: 'A balanced challenge for consistent traders',
    bestFor: 'Intermediate traders',
    features: [
      'EOD Balance drawdown',
      'Up to 80% profit split',
      '5 funded days minimum',
      'No activation fee',
    ],
  },
  {
    tier: 'Professional',
    tagline: 'Larger accounts with more rules and higher targets',
    bestFor: 'Experienced traders',
    features: [
      'EOD Balance drawdown',
      'Up to 90% profit split',
      '8 funded days minimum',
      'Priority support',
    ],
    badge: 'Top split',
  },
];

export interface SizeOption {
  size: SizeKey;
  price: number; // challenge fee
  resetPrice: number;
  profitTarget: number;
  maxDrawdown: number;
  dailyDrawdown: number;
  profitShare: number;
  fundedFee: number;
  monthlyFeeIncluded: boolean;
}

export const SIZE_OPTIONS_BY_TIER: Record<TierName, SizeOption[]> = {
  Beginner: [
    { size: '50', price: 79, resetPrice: 69, profitTarget: 3000, maxDrawdown: 2000, dailyDrawdown: 1000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '100', price: 149, resetPrice: 129, profitTarget: 6000, maxDrawdown: 3000, dailyDrawdown: 2000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '150', price: 219, resetPrice: 189, profitTarget: 9000, maxDrawdown: 4500, dailyDrawdown: 3000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
  ],
  Mid: [
    { size: '50', price: 89, resetPrice: 79, profitTarget: 3000, maxDrawdown: 2000, dailyDrawdown: 1000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '100', price: 159, resetPrice: 139, profitTarget: 6000, maxDrawdown: 3000, dailyDrawdown: 2000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '150', price: 229, resetPrice: 199, profitTarget: 9000, maxDrawdown: 4500, dailyDrawdown: 3000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
  ],
  Professional: [
    { size: '50', price: 219, resetPrice: 189, profitTarget: 3000, maxDrawdown: 2000, dailyDrawdown: 1000, profitShare: 90, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '100', price: 379, resetPrice: 339, profitTarget: 6000, maxDrawdown: 3000, dailyDrawdown: 2000, profitShare: 90, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '150', price: 539, resetPrice: 489, profitTarget: 9000, maxDrawdown: 4500, dailyDrawdown: 3000, profitShare: 90, fundedFee: 0, monthlyFeeIncluded: true },
  ],
};

export interface PlatformOption {
  key: PlatformKey;
  displayName: string;
  description: string;
  bullets: string[];
  badge?: string;
}

export const PLATFORM_OPTIONS: PlatformOption[] = [
  {
    key: 'Deepchart',
    displayName: 'Deepchart®',
    description: 'Web & desktop charting with volume profile, TPO and order flow tools.',
    bullets: ['Volume Profile & TPO', 'Advanced DOM', 'Order flow indicators', 'Windows desktop'],
    badge: 'Most popular',
  },
  {
    key: 'ATAS',
    displayName: 'ATAS',
    description: 'Order flow analysis, DOM heatmap and cluster charts for futures.',
    bullets: ['Cluster charts', 'DOM heatmap', 'Order flow analysis', 'Multi-broker'],
  },
  {
    key: 'Deepdom',
    displayName: 'Deepdom®',
    description: 'Pure DOM trading with depth-of-market, iceberg detection and footprint charts.',
    bullets: ['DOM trading', 'Iceberg detection', 'Footprint charts', 'Order flow viz'],
  },
  {
    key: 'Quantower',
    displayName: 'Quantower',
    description: 'Multi-asset analytics with built-in DOM, depth-of-market and trade simulator.',
    bullets: ['Multi-asset analytics', 'Built-in DOM', 'Volume tools', 'Windows / Mac'],
  },
  {
    key: 'Bookmap',
    displayName: 'Bookmap',
    description: 'Heatmap and order book visualization with iceberg and sweep detection.',
    bullets: ['Heatmap', 'Iceberg detection', 'Sweep alerts', 'Historical replay'],
  },
];

export function getSizeOption(tier: TierName, size: SizeKey): SizeOption | undefined {
  return SIZE_OPTIONS_BY_TIER[tier]?.find((s) => s.size === size);
}

export function getPlatformOption(key: PlatformKey): PlatformOption | undefined {
  return PLATFORM_OPTIONS.find((p) => p.key === key);
}
